import { request } from '@core/api';
import { errorBox } from '@core/components/AlertBox';
import { environment } from '@core/environment';
import type { ExternalDataTrench } from '@core/types';
import { GrpcRestFetchTransport, useAuth0Interceptor } from '@deepup/api-utils';
import { commonApiV1, iconstructionGeojsonClientApiV1, structApi } from '@deepup/internal-apis';
import type { JsonValue } from '@protobuf-ts/runtime';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { proxy, useSnapshot } from 'valtio';

interface ExternalDataState {
  isExternalVisible: boolean;
  cachedTrenches: ExternalDataTrench[];
}

const toggleState = proxy<ExternalDataState>({ isExternalVisible: false, cachedTrenches: [] });

const toggleExternalVisible = (): void => {
  toggleState.isExternalVisible = !toggleState.isExternalVisible;
};

const setCachedTrenches = (trenches: ExternalDataTrench[]) => {
  toggleState.cachedTrenches = trenches;
};

export const useExternalDataState = () => ({
  ...useSnapshot(toggleState),
  toggleExternalVisible,
});

export const cleanExternalDataCache = () => setCachedTrenches([]);

type GeoJsonGeometry = {
  type: string;
  coordinates?: JsonValue;
  geometries?: GeoJsonGeometry[];
  bbox?: JsonValue;
};

type GeoJsonFeature = {
  id?: string | number;
  type: string;
  geometry?: GeoJsonGeometry | null;
  properties?: JsonValue | null;
  bbox?: JsonValue;
};

export type GeoJsonFeatureCollection = {
  type: string;
  features?: GeoJsonFeature[];
  bbox?: JsonValue;
};

const toListValue = (value: JsonValue | undefined): structApi.ListValue | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return structApi.ListValue.internalJsonRead(value, { ignoreUnknownFields: true });
};

const toStruct = (value: JsonValue | null | undefined): structApi.Struct | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  return structApi.Struct.internalJsonRead(value, { ignoreUnknownFields: true });
};

const toGeometry = (
  geometry: GeoJsonGeometry | null | undefined,
): commonApiV1.Geometry | undefined => {
  if (!geometry) {
    return undefined;
  }

  return {
    type: geometry.type,
    coordinates:
      geometry.type === 'GeometryCollection' ? undefined : toListValue(geometry.coordinates),
    geometries:
      geometry.type === 'GeometryCollection'
        ? (geometry.geometries ?? [])
            .map((item) => toGeometry(item))
            .filter((item): item is commonApiV1.Geometry => Boolean(item))
        : [],
    bbox: toListValue(geometry.bbox),
  };
};

const toFeature = (feature: GeoJsonFeature): commonApiV1.Feature => ({
  id: feature.id === undefined ? '' : String(feature.id),
  type: feature.type,
  geometry: toGeometry(feature.geometry),
  bbox: toListValue(feature.bbox),
  properties: toStruct(feature.properties),
});

const toFeatureCollection = (geoJson: GeoJsonFeatureCollection): commonApiV1.FeatureCollection => ({
  type: geoJson.type,
  features: (geoJson.features ?? []).map((feature) => toFeature(feature)),
  bbox: toListValue(geoJson.bbox),
});

export const useCreateTrenchesFromGeoJson = () => {
  const authInterceptor = useAuth0Interceptor();
  const client = useMemo(() => {
    const transportRest = new GrpcRestFetchTransport({
      baseUrl: environment.unifiedApiUrl,
      fetchInit: {
        credentials: 'include',
      },
      interceptors: [authInterceptor],
    });

    return new iconstructionGeojsonClientApiV1.InternalConstructionGeoJsonServiceClient(
      transportRest,
    );
  }, [authInterceptor]);

  const createTrenchesFromGeoJson = async (
    projectId: string,
    geoJson: GeoJsonFeatureCollection,
  ) => {
    if (geoJson.type !== 'FeatureCollection' || !Array.isArray(geoJson.features)) {
      throw new Error('Invalid GeoJSON FeatureCollection');
    }

    const trenches = toFeatureCollection(geoJson);
    return client.createTrenchesFromGeoJson({ projectId, scanId: '', trenches }).response;
  };

  return { createTrenchesFromGeoJson };
};

export const useExternalDataTrenches = () => {
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const { isExternalVisible } = useExternalDataState();

  useEffect(() => {
    if (!isExternalVisible || toggleState.cachedTrenches.length) {
      // Skip fetching data if can use cached external data or the toggle is OFF
      return;
    }

    async function fetchData() {
      try {
        const response = await request<ExternalDataTrench[]>('GET', {
          path: ['projects', projectId!, 'externalData'],
        });
        if (response.length) {
          setCachedTrenches(response);
        }
      } catch (_error) {
        errorBox('Error fetching external data');
      }
    }

    fetchData();
  }, [projectId, toggleState.cachedTrenches]);

  return {
    trenches: toggleState.cachedTrenches,
  };
};
